import * as React from 'react'
import { CalendarX, Loader2 } from 'lucide-react'
import { usePurgeVenue, useVenuePurgePreview } from '@/hooks/useEvents'
import { requestErrorMessage } from '@/helpers/apiClient'
import { venuePurgeSummary } from '@/helpers/eventDelete'
import { Button } from '@/components/ui/button'
import { ChoiceCard, ChoiceGroup } from '@/components/ui/choice-card'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/components/ui/toast'
import { FormError } from '@/components/ui/form-error'
import { Card } from '@/components/ui/card'
import { VENUE_PURGE_WORD, type PurgeVenueEventsInput } from '@lib/input/venue-purge.input'
import type { VenuePurgeCounts } from '@lib/dto/venue-purge.dto'
import type { VenueDto } from '@lib/dto/venue.dto'

/** How many client contacts the venue's events name at all. */
function clientsTouched(counts: VenuePurgeCounts): number {
  return counts.clientsDeleted + counts.clientsDeactivated + counts.clientsKept
}

/**
 * Org-admin danger-zone action: delete every event at a venue, and optionally the
 * client contacts on them.
 *
 * For a venue whose calendar came in wrong — an import that named the rooms badly,
 * or a season nobody wants any more. The venue itself survives, so it can be
 * imported into again, which is the difference between this and archiving it.
 *
 * Owns its confirm state, the preview it reads to price the two choices, and the
 * mutation, so settings stays pure composition.
 */
export function VenueClearEventsAction({ venue }: { venue: VenueDto }): React.ReactElement {
  const { toast } = useToast()
  const [open, setOpen] = React.useState(false)
  const [clients, setClients] = React.useState<PurgeVenueEventsInput['clients']>('remove')
  const [typed, setTyped] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  // Counted while the dialog is open only: the confirmation is a promise about
  // what is there now, and a count read on page load would be stale by the time
  // anyone acts on it.
  const preview = useVenuePurgePreview(venue.id, open)
  const purge = usePurgeVenue()
  const counts = preview.data?.counts
  const nothingToClear = counts != null && counts.events === 0 && clientsTouched(counts) === 0

  function close(): void {
    setOpen(false)
    setTyped('')
    setError(null)
    setClients('remove')
  }

  function submit(): void {
    // The keep/remove choice is only on screen once the preview has loaded.
    // Until then `clients` is still the default, "remove", which the person
    // never chose — and a failed preview must not delete accounts anyway.
    if (!preview.isSuccess || counts == null) return
    setError(null)
    purge.mutate(
      { venueId: venue.id, input: { confirm: VENUE_PURGE_WORD, clients } },
      {
        onSuccess: (result) => {
          toast(venuePurgeSummary(result.counts), { variant: 'warning' })
          close()
        },
        onError: (e) => setError(requestErrorMessage(e)),
      },
    )
  }

  return (
    <Card className="flex flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 space-y-1">
        <p className="text-sm font-medium">Clear this venue’s calendar</p>
        <p className="text-sm text-ink-muted">
          Permanently delete every event at “{venue.name}” — their phases, floor plans, documents and
          rentals — and optionally the client contacts on them. The venue, its spaces, tracks and
          inventory stay, ready to be imported into again.
        </p>
      </div>
      {/* Outlined in danger ink, as "Delete event" is; the filled red button is
          the one in the dialog that confirms. */}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="shrink-0 text-danger-ink hover:bg-danger-soft hover:text-danger-ink"
        onClick={() => {
          setError(null)
          setOpen(true)
        }}
      >
        <CalendarX className="h-4 w-4" />
        Clear events
      </Button>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) close()
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Clear every event at {venue.name}?</DialogTitle>
            <DialogDescription>
              This deletes the events below, along with their phases, floor plans, documents and
              rentals. It cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {preview.isPending ? (
            <p className="flex items-center gap-2 text-sm text-ink-muted">
              <Loader2 className="h-4 w-4 animate-spin" /> Counting what would go…
            </p>
          ) : null}
          {preview.isError ? <FormError>{requestErrorMessage(preview.error)}</FormError> : null}

          {counts ? (
            <div className="space-y-3">
              <Card className="flex items-start justify-between gap-3 p-3 text-sm">
                <span className="min-w-0">
                  <span className={counts.events === 0 ? 'text-ink-muted' : ''}>Events</span>
                  <span className="block text-xs text-ink-muted">
                    with their plans, documents and rentals
                  </span>
                </span>
                <span className="shrink-0 font-medium tabular-nums">{counts.events}</span>
              </Card>

              {clientsTouched(counts) > 0 ? (
                <ChoiceGroup legend="Their client accounts">
                  <ChoiceCard
                    type="radio"
                    name="purge-clients"
                    checked={clients === 'remove'}
                    onSelect={() => setClients('remove')}
                    title="Remove their client accounts too"
                  >
                    <ul className="m-0 mt-2 list-none space-y-1 p-0 text-sm">
                      <li className="flex items-start justify-between gap-3">
                        <span className="min-w-0">
                          Invitations withdrawn
                          <span className="block text-xs text-ink-muted">
                            frees each address to be invited again
                          </span>
                        </span>
                        <span className="shrink-0 font-medium tabular-nums">{counts.clientsDeleted}</span>
                      </li>
                      <li className="flex items-start justify-between gap-3">
                        <span className="min-w-0">
                          Accounts deactivated
                          <span className="block text-xs text-ink-muted">
                            they have signed in, so their history is kept
                          </span>
                        </span>
                        <span className="shrink-0 font-medium tabular-nums">{counts.clientsDeactivated}</span>
                      </li>
                      <li className="flex items-start justify-between gap-3">
                        <span className="min-w-0">
                          Accounts kept
                          <span className="block text-xs text-ink-muted">
                            still contacts at another venue
                          </span>
                        </span>
                        <span className="shrink-0 font-medium tabular-nums">{counts.clientsKept}</span>
                      </li>
                    </ul>
                  </ChoiceCard>
                  <ChoiceCard
                    type="radio"
                    name="purge-clients"
                    checked={clients === 'keep'}
                    onSelect={() => setClients('keep')}
                    title="Keep the client accounts"
                    description={`${String(clientsTouched(counts))} client account${clientsTouched(counts) === 1 ? '' : 's'} will be left signed up, with nothing booked here.`}
                  />
                </ChoiceGroup>
              ) : null}
            </div>
          ) : null}

          <Field
            label={`Type ${VENUE_PURGE_WORD} to confirm`}
            htmlFor="purge-confirm"
            hint={nothingToClear ? 'There is nothing to clear.' : undefined}
          >
            <Input
              id="purge-confirm"
              value={typed}
              autoComplete="off"
              onChange={(e) => setTyped(e.target.value)}
              placeholder={VENUE_PURGE_WORD}
            />
          </Field>
          {error ? <FormError>{error}</FormError> : null}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={
                typed !== VENUE_PURGE_WORD ||
                purge.isPending ||
                !preview.isSuccess ||
                nothingToClear
              }
              onClick={submit}
              pending={purge.isPending}
            >
              <CalendarX className="h-4 w-4" />
              {purge.isPending ? 'Clearing…' : 'Clear events'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
